"use client";

import { Check, X } from "lucide-react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export interface BulkLineResult {
  line: string;
  ok: boolean;
  error?: string;
}

export interface BulkSummary {
  results: BulkLineResult[];
  added: number;
  droppedCount: number;
}

interface BulkResultSummaryProps {
  summary: BulkSummary;
}

export function BulkResultSummary({ summary }: BulkResultSummaryProps) {
  const s = useToolStrings();
  const { results, added, droppedCount } = summary;
  const failed = results.length - added;

  return (
    <Card className="animate-fade-in">
      <CardHeader className="pb-3">
        <CardTitle>{s.bulkResults}</CardTitle>
        <CardDescription>
          {added} added to your bibliography
          {failed > 0 ? `, ${failed} failed` : ""}
          {droppedCount > 0
            ? ` — ${droppedCount} line${droppedCount === 1 ? "" : "s"} skipped (over the limit)`
            : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {results.map((result, index) => (
            <li
              className="flex items-start gap-2 text-sm"
              key={`${index}-${result.line}`}
            >
              {result.ok ? (
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <X className="mt-0.5 size-4 shrink-0 text-destructive" />
              )}
              <span className="break-all font-mono text-xs">
                {result.line}
                {result.ok ? null : (
                  <span className="text-muted-foreground">
                    {" — "}
                    {result.error ?? "could not be cited"}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
